import { percentualParaTaxa, type AjustesCenario, type CenarioResponse, type ComposicaoPrecoResponse } from '@triade/contracts';
import { decimalDigitado } from '@/lib/format';
import type { AvisoMotor, ResultadoMotor } from '@/components/formacao/tipos';

/** Campos de premissa de um cenario. Texto como o usuario digita; vazio = "usar o valor atual (base)". */
export const CAMPOS = ['custoVariacao', 'lucroDesejado', 'despesaVariavel', 'despesaFixa', 'taxaFinanceira', 'custo', 'praticado'] as const;
export type Campo = (typeof CAMPOS)[number];

export interface CenarioForm {
  id: number;
  nome: string;
  campos: Record<Campo, string>;
}

export function cenarioVazio(id: number): CenarioForm {
  return { id, nome: '', campos: { custoVariacao: '', lucroDesejado: '', despesaVariavel: '', despesaFixa: '', taxaFinanceira: '', custo: '', praticado: '' } };
}

export function temAjustes(c: CenarioForm): boolean {
  return CAMPOS.some((k) => c.campos[k].trim() !== '');
}

/**
 * Percentual digitado com sinal opcional ("+8", "-10,5", "8") para fracao com sinal ("0.08", "-0.105").
 * So desloca o ponto decimal na string (delegado a `percentualParaTaxa`); invalido ou acima de 100%: null.
 */
export function percentualComSinal(texto: string): string | null {
  const t = texto.trim();
  const neg = t.startsWith('-');
  const f = percentualParaTaxa(t.replace(/^[+-]/, ''));
  if (f === null) return null;
  return neg && f !== '0' ? `-${f}` : f;
}

const PERCENTUAIS: Array<Exclude<Campo, 'custoVariacao' | 'custo' | 'praticado'>> = ['lucroDesejado', 'despesaVariavel', 'despesaFixa', 'taxaFinanceira'];

/** Converte os textos do cenario nos `ajustes` do contrato (strings decimais). Campos invalidos saem em `invalidos`. */
export function paraAjustes(c: CenarioForm): { ajustes: AjustesCenario; invalidos: Campo[] } {
  const ajustes: AjustesCenario = {};
  const invalidos: Campo[] = [];
  const preenchido = (k: Campo) => c.campos[k].trim() !== '';

  if (preenchido('custoVariacao')) {
    const v = percentualComSinal(c.campos.custoVariacao);
    if (v === null) invalidos.push('custoVariacao');
    else ajustes.custoVariacao = v;
  }
  for (const k of PERCENTUAIS) {
    if (!preenchido(k)) continue;
    const v = percentualParaTaxa(c.campos[k]);
    if (v === null) invalidos.push(k);
    else ajustes[k] = v;
  }
  for (const k of ['custo', 'praticado'] as const) {
    if (!preenchido(k)) continue;
    const v = decimalDigitado(c.campos[k]);
    if (v === null) invalidos.push(k);
    else ajustes[k] = v;
  }
  return { ajustes, invalidos };
}

export interface CenarioLido {
  nome: string;
  erro: { code: string; message: string } | null;
  resultado: ResultadoMotor | null;
  composicao: ComposicaoPrecoResponse | null;
  avisos: AvisoMotor[];
  diferencas: CenarioResponse['diferencas'];
}

/** Forma minima de um cenario da API (resultado e composicao vem como unknown/opcional no contrato). */
export function lerCenario(c: CenarioResponse): CenarioLido {
  const avisos = (c.warnings as Array<Partial<AvisoMotor>>).flatMap((w) =>
    typeof w.code === 'string' ? [{ code: w.code, message: String(w.message ?? w.code) }] : [],
  );
  return { nome: c.nome, erro: c.erro, resultado: c.resultado as ResultadoMotor | null, composicao: c.composicao, avisos, diferencas: c.diferencas };
}
